// src/components/Studio/MemberCard.tsx
import { Link } from 'react-router-dom';
import { MapPin, Star, User } from 'lucide-react';
import type { MemberProfile } from '../../types/studio';
import { AVAILABILITY_LABEL, AVAILABILITY_TONE, PANEL, PANEL_HOVER } from './studioStyles';

const MemberCard = ({ member }: { member: MemberProfile }) => {
  const availabilityTone = AVAILABILITY_TONE[member.availability] ?? AVAILABILITY_TONE.UNAVAILABLE;

  return (
    <Link
      to={`/community/troupe/${member.slug}`}
      className={`group block overflow-hidden ${PANEL} ${PANEL_HOVER} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400`}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-neutral-900">
        {member.avatarUrl ? (
          <img
            src={member.avatarUrl}
            alt=""
            className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:scale-[1.03] group-hover:grayscale-0"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <User className="h-10 w-10 text-neutral-700" aria-hidden="true" />
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/85 to-transparent" />

        {member.foundingMember && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/20 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-200">
            <Star className="h-3 w-3" aria-hidden="true" />
            Founding
          </span>
        )}

        <div className="absolute inset-x-0 bottom-0 p-4">
          <h3 className="font-display text-base font-bold leading-tight text-white line-clamp-1">
            {member.displayName}
          </h3>
          <p className="mt-0.5 text-xs font-medium uppercase tracking-wider text-orange-400">
            {member.primaryCraftLabel}
          </p>
        </div>
      </div>

      <div className="p-4">
        {member.headline && (
          <p className="text-sm leading-relaxed text-neutral-400 line-clamp-2">{member.headline}</p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${availabilityTone}`}
          >
            {AVAILABILITY_LABEL[member.availability] ?? 'Availability unknown'}
          </span>
          {member.productionCount > 0 && (
            <span className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-neutral-400">
              {member.productionCount} production{member.productionCount === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {member.location && (
          <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-neutral-500">
            <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
            {member.location}
          </p>
        )}
      </div>
    </Link>
  );
};

export default MemberCard;
